import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { PriceMatrixEntry } from '@/lib/PriceMatrix';
import type { Teacher } from '@/lib/Teacher';
import type { Subject } from '@/lib/Subject';
import { getSubjectPrettyName } from '@/lib/Subject';
import { t } from '@/lib/i18n';
import { Box, Button, Heading, HStack, Table, Text, VStack } from '@chakra-ui/react';
import { Fragment } from 'react';
import { useColorModeValue } from '@/components/ui/color-mode';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState, useCallback } from 'react';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
    });
}

export function PriceMatrix() {
    const { getAccessTokenSilently } = useAuth0();

    const { data: teachersData, error: teachersError, isLoading: teachersLoading, isAuthenticated } =
        useAuthorizedRequest<{ teachers: Teacher[] }>('/teachers', jsonFetcher);
    const { data: subjectsData, error: subjectsError } =
        useAuthorizedRequest<{ subjects: Subject[] }>('/subjects', jsonFetcher);
    const { data: priceData, error: priceError, isLoading: priceLoading, mutate } =
        useAuthorizedRequest<{ entries: PriceMatrixEntry[] }>('/price-matrix', jsonFetcher);

    const teachers = useMemo(() => (teachersData?.teachers ?? []).filter(t => !t.deleted).sort((a, b) => a.name.localeCompare(b.name)), [teachersData]);
    const subjects = useMemo(() => (subjectsData?.subjects ?? []).filter(s => !s.deleted).sort((a, b) => a.name.localeCompare(b.name)), [subjectsData]);
    const entries = useMemo(() => priceData?.entries ?? [], [priceData]);

    // Build a lookup map: "teacher_id:subject" → entry
    const entryMap = useMemo(() => {
        const map: Record<string, PriceMatrixEntry> = {};
        for (const e of entries) {
            map[`${e.teacher_id}:${e.subject}`] = e;
        }
        return map;
    }, [entries]);

    // Local editable state: "teacher_id:subject" → { price_uah, duration_minutes }
    const [dirty, setDirty] = useState<Record<string, { price_uah: number; duration_minutes: number }>>({});
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    const getValue = useCallback((teacherId: string, subject: string) => {
        const key = `${teacherId}:${subject}`;
        if (dirty[key]) return dirty[key]!;
        const entry = entryMap[key];
        return entry ? { price_uah: entry.price_uah, duration_minutes: entry.duration_minutes } : null;
    }, [dirty, entryMap]);

    const setValue = useCallback((teacherId: string, subject: string, field: 'price_uah' | 'duration_minutes', value: number) => {
        setDirty(prev => {
            const key = `${teacherId}:${subject}`;
            const current = prev[key] ?? getValue(teacherId, subject) ?? { price_uah: 0, duration_minutes: 0 };
            return { ...prev, [key]: { ...current, [field]: value } };
        });
    }, [getValue]);

    async function handleSave() {
        setSaving(true);
        setSaveMessage('');
        try {
            const token = await getAccessTokenSilently();
            const entriesList: PriceMatrixEntry[] = [];
            for (const teacher of teachers) {
                for (const subject of subjects) {
                    const key = `${teacher.id}:${subject.name}`;
                    const val = dirty[key] ?? getValue(teacher.id, subject.name) ?? entryMap[key];
                    if (val) {
                        entriesList.push({
                            teacher_id: teacher.id,
                            subject: subject.name,
                            price_uah: val.price_uah,
                            duration_minutes: val.duration_minutes,
                        });
                    }
                }
            }
            await apiRequest('PUT', '/price-matrix', token, { entries: entriesList });
            setSaveMessage(t('price_matrix.saved'));
            setDirty({});
            await mutate();
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (e) {
            setSaveMessage(`${t('label.error')}: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            setSaving(false);
        }
    }

    // All hooks must be called before any early return (Rules of Hooks)
    const inputBg = useColorModeValue('white', '#1a1a2e');
    const inputBorder = useColorModeValue('#e2e8f0', '#2a2a4a');

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (teachersError || subjectsError || priceError) return <div>{t('label.error')}: {(teachersError || subjectsError || priceError)!.message}</div>;

    if (teachers.length === 0 || subjects.length === 0) {
        return (
            <VStack gap={4} align="stretch">
                <Heading size="md">{t('price_matrix.title')}</Heading>
                <Text color="gray.500">{t('price_matrix.no_data')}</Text>
            </VStack>
        );
    }

    return (
        <VStack gap={4} align="stretch">
            <HStack justify="space-between">
                <Heading size="md">{t('price_matrix.title')}</Heading>
                <HStack>
                    {saveMessage && <Text fontSize="sm" color={saveMessage.includes('error') || saveMessage.includes('Error') ? 'red.500' : 'green.500'}>{saveMessage}</Text>}
                    <Button size="sm" onClick={handleSave} loading={saving}>{t('price_matrix.save')}</Button>
                </HStack>
            </HStack>
            <Box overflowX="auto">
                <Table.Root variant="line" showColumnBorder interactive size="sm">
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>{t('price_matrix.teacher')}</Table.ColumnHeader>
                            {subjects.map(s => (
                                <Table.ColumnHeader key={s.id} textAlign="center" colSpan={2}>
                                    {getSubjectPrettyName(s.name)}
                                </Table.ColumnHeader>
                            ))}
                        </Table.Row>
                        <Table.Row>
                            <Table.ColumnHeader></Table.ColumnHeader>
                            {subjects.map(s => (
                                <Fragment key={s.id}>
                                    <Table.ColumnHeader fontSize="xs" textAlign="center">{t('price_matrix.price')}</Table.ColumnHeader>
                                    <Table.ColumnHeader fontSize="xs" textAlign="center">{t('price_matrix.duration')}</Table.ColumnHeader>
                                </Fragment>
                            ))}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {teachers.map(teacher => (
                            <Table.Row key={teacher.id}>
                                <Table.Cell fontWeight="medium">{teacher.name}</Table.Cell>
                                {subjects.map(subject => {
                                    const val = getValue(teacher.id, subject.name);
                                    return (
                                        <Fragment key={subject.id}>
                                            <Table.Cell p={1}>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="₴"
                                                    value={val?.price_uah ?? ''}
                                                    onChange={e => setValue(teacher.id, subject.name, 'price_uah', parseInt(e.target.value) || 0)}
                                                    style={{
                                                        width: '70px',
                                                        padding: '4px 6px',
                                                        borderRadius: '4px',
                                                        border: `1px solid ${inputBorder}`,
                                                        background: inputBg,
                                                        color: 'inherit',
                                                        fontSize: '13px',
                                                        textAlign: 'center',
                                                    }}
                                                />
                                            </Table.Cell>
                                            <Table.Cell p={1}>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="хв"
                                                    value={val?.duration_minutes ?? ''}
                                                    onChange={e => setValue(teacher.id, subject.name, 'duration_minutes', parseInt(e.target.value) || 0)}
                                                    style={{
                                                        width: '60px',
                                                        padding: '4px 6px',
                                                        borderRadius: '4px',
                                                        border: `1px solid ${inputBorder}`,
                                                        background: inputBg,
                                                        color: 'inherit',
                                                        fontSize: '13px',
                                                        textAlign: 'center',
                                                    }}
                                                />
                                            </Table.Cell>
                                        </Fragment>
                                    );
                                })}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </Box>
        </VStack>
    );
}
